import { Catalogue } from './catalogues';
import {
  ShiftType,
  EmailType,
  RoundOffOption,
  RoundOffValue,
  TipCalculation,
  ReceiptPrintOption,
  SignatureAndTippingOption,
  AppScreen,
  DeviceProfileStatus,
  PrinterType,
  PrinterBrand,
  PrinterSeries,
  KitchenDisplayMode,
  DeviceMode,
  OrderIdentifier,
  ThemeType,
  CustomerSearchMode,
  MediaType,
  Theme,
  OrbAutoClearTimeout,
  ModifierKeywordMatchMode,
} from '../enums';
import { FunctionMap } from './functionMaps';
import { Order } from './orders';
import { Address, Section, Venue } from './venues';
import { PrintingOptions } from './printerTemplate';
import { OrderType } from './organizations';
import { TimeBlock } from './schedule';
import { PricingGroup } from './pricing';
import { PrinterProfile, Product, Variant } from '.';
import { LayoutStrategyConfig } from '../inputs';

export interface GridSize {
  rows: number;
  columns: number;
}

export enum GridType {
  PRODUCTS = 'products',
  OPTIONS = 'options',
}

export interface EmailPreference {
  emailType: EmailType;
  isEnabled: boolean;
  emails?: string[];
}
export interface KdsNotificationConfig {
  notifyOnBump?: boolean;
  notifyOnReceive?: boolean;
  bumpMessage?: string;
  receiveMessage?: string;
  notifyOnBumpOrderTypes?: string[];
  notifyOnReceiveOrderTypes?: string[];
}
export interface KdsTimerMilestone {
  minutes: number;
  label: string;
  color: string;
}

/**
 * A single condition used to match a modifier's display text to a `KdsModifierStyle`.
 *
 * `matchType` determines how `keyword` is compared against the modifier text:
 * `EXACT` requires the modifier text to equal `keyword` exactly (case-insensitive);
 * `CONTAINS` matches if `keyword` appears anywhere within the modifier text (case-insensitive).
 */
export interface KdsModifierCondition {
  keyword: string;
  matchType: ModifierKeywordMatchMode;
}

/**
 * The visual appearance applied to a matched modifier — colour and/or text style.
 *
 * `color` is one of the six fixed palette hex values from FR-4 (validated server-side,
 * not a free colour picker). `color` and `italic` are both optional, but at least one
 * must be set on the parent `KdsModifierStyle` — a style with neither has no visible
 * effect and is rejected by `validateModifierStyles`.
 *
 * `background` tints the modifier's background using `color` at reduced opacity —
 * it has no effect without `color` set, and is rejected by `validateModifierStyles`
 * if `color` is absent.
 */
export interface KdsModifierStyleAppearance {
  color?: string;
  italic?: boolean;
  background?: boolean;
}

/**
 * A device-specific rule that renders matching modifiers (see `KdsModifierCondition`)
 * in a distinct colour and/or text style on the KDS ticket.
 *
 * `isActive` lets a style be temporarily disabled without losing its configuration;
 * it still counts toward the max-3-styles-per-device limit.
 *
 * `priority` determines precedence when a modifier matches more than one style —
 * the style with the lowest `priority` wins. Values must be unique per device but
 * need not be contiguous (e.g. `1, 3, 4` is valid).
 */
export interface KdsModifierStyle {
  id: string;
  name: string;
  styles?: KdsModifierStyleAppearance;
  isActive?: boolean;
  priority: number;
  /**
   * When `true`, this style applies to every modifier and `conditions` is
   * ignored (it may still hold a previously-typed draft so the UI can switch
   * back to keyword mode without losing it).
   */
  matchAll?: boolean;
  conditions: KdsModifierCondition[];
}

export interface InventoryOptions {
  allowSellingWithNegativeInventory: boolean;
}

export interface TipOption {
  name: string;
  value: number;
}

export const DEFAULT_TIP_OPTIONS: TipOption[] = [
  { name: 'Good', value: 5 },
  { name: 'Best', value: 10 },
  { name: 'Amazing!', value: 15 },
];

export interface CheckoutOptions {
  enableRoundOff: boolean;
  roundingMethod: RoundOffOption;
  roundOffValue: RoundOffValue;
  enableTips: boolean;
  tipCalculation: TipCalculation;
  withdrawTipAtClosureTime: boolean;
  showQRPay?: boolean;
  tipOptions?: TipOption[];
  enablePaymentByLink?: boolean;
  paymentLinkMessageTemplate?: string;
}

/**
 * Tokens insertable into `CheckoutOptions.paymentLinkMessageTemplate` as
 * `{{token}}` placeholders. `{{paymentLink}}` is mandatory; the rest are optional.
 */
export const PAYMENT_LINK_TEMPLATE_TOKENS = [
  'customerName',
  'orderNumber',
  'venueName',
  'amountDue',
  'paymentLink',
] as const;

export type PaymentLinkTemplateToken =
  (typeof PAYMENT_LINK_TEMPLATE_TOKENS)[number];

export const DEFAULT_PAYMENT_LINK_MESSAGE_TEMPLATE =
  'Hi {{customerName}}, your payment link for order #{{orderNumber}} at {{venueName}} is ready! Amount due: {{amountDue}}. Use the link below to pay securely. {{paymentLink}}';

const PAYMENT_LINK_TEMPLATE_TOKEN_PATTERN = /{{([^{}]*)}}/g;

/**
 * `{{...}}` placeholders in the template that aren't in
 * `PAYMENT_LINK_TEMPLATE_TOKENS` — e.g. a typo like `{{customerNam}}` or an
 * unsupported name like `{{customer-name}}` — which would otherwise be sent
 * to customers unresolved. Matches the full contents of each placeholder
 * (not just `\w` characters) so malformed tokens can't slip through unseen.
 */
export function getUnknownPaymentLinkTemplateTokens(
  template: string,
): string[] {
  const found: string[] = [];
  let match: RegExpExecArray | null;
  const pattern = new RegExp(PAYMENT_LINK_TEMPLATE_TOKEN_PATTERN);
  while ((match = pattern.exec(template))) {
    found.push(match[1].trim());
  }
  return Array.from(
    new Set(
      found.filter(
        token =>
          !PAYMENT_LINK_TEMPLATE_TOKENS.includes(
            token as PaymentLinkTemplateToken,
          ),
      ),
    ),
  );
}

/**
 * The single source of truth for the payment-link template contract: a
 * template is only valid once it includes the mandatory `{{paymentLink}}`
 * token and every other `{{token}}` placeholder it uses is a recognized one.
 * Shared between the Back Office UI and the venues service so both layers
 * enforce the same rule.
 */
export function isPaymentLinkTemplateValid(template: string): boolean {
  return (
    template.includes('{{paymentLink}}') &&
    getUnknownPaymentLinkTemplateTokens(template).length === 0
  );
}

export interface OperatingHours {
  id?: string;
  name?: string;
  timeBlocks: TimeBlock[];
}

export interface ProfitMargin {
  productTypeId: string;
  margin: number;
}

export interface PayStoreRef {
  online?: string;
  instore?: string;
}

// Matches pay/proto/common.proto's Acquirer enum names.
export enum PayAcquirer {
  ADYEN = 'ADYEN',
  WPAY = 'WPAY',
  ADYEN_UK = 'ADYEN_UK',
  ADYEN_EU = 'ADYEN_EU',
  ADYEN_MANAGED = 'ADYEN_MANAGED',
  ADYEN_NZ = 'ADYEN_NZ',
  ADYEN_US = 'ADYEN_US',
  ADYEN_GOLFBOX = 'ADYEN_GOLFBOX',
  PYNG_AU = 'PYNG_AU',
  TYRO = 'TYRO',
}

// Merchant codes for Oolio's shared Adyen merchant accounts. Each region with
// its own Adyen entity (UK, NZ, US) has its own code; referenced refunds fail
// if a store uses the wrong region's code. DEFAULT covers acquirers that
// share Oolio's original AU account (ADYEN, ADYEN_GOLFBOX).
// See @oolio-group/pay-service's getStoreMerchant for which acquirers get it.
export const MERCHANT_CODES = {
  DEFAULT: 'OolioGroup_AFP',
  UK: 'OolioGroup_UK_AFP',
  NZ: 'OolioGroup_NZ_AFP',
  US: 'OolioGroup_US_AFP',
} as const;

// These acquirers share one Oolio-owned Adyen merchant account per acquirer,
// so the referenced-refunds merchant code is fixed rather than store-specific.
const SHARED_MERCHANT_CODE_BY_ACQUIRER: Partial<Record<PayAcquirer, string>> = {
  [PayAcquirer.ADYEN]: MERCHANT_CODES.DEFAULT,
  [PayAcquirer.ADYEN_US]: MERCHANT_CODES.US,
  [PayAcquirer.ADYEN_UK]: MERCHANT_CODES.UK,
  [PayAcquirer.ADYEN_NZ]: MERCHANT_CODES.NZ,
  [PayAcquirer.ADYEN_GOLFBOX]: MERCHANT_CODES.DEFAULT,
};

export const isSharedMerchantCodeAcquirer = (acquirer?: PayAcquirer): boolean =>
  acquirer != undefined && acquirer in SHARED_MERCHANT_CODE_BY_ACQUIRER;

export const getSharedMerchantCode = (
  acquirer?: PayAcquirer,
): string | undefined =>
  acquirer != undefined
    ? SHARED_MERCHANT_CODE_BY_ACQUIRER[acquirer]
    : undefined;

export interface MerchantSettings {
  legalName?: string;
  tradingName?: string;
  acquirer?: PayAcquirer;
  refundsEnabled?: boolean;
  onboardingStatus?: string;
}

export interface PaySalesChannelStatus {
  pos?: boolean;
  online?: boolean;
}

export interface PayMerchantConfig {
  isOutOfSync?: boolean;
  tradingName?: string;
}

export interface Store {
  id: string;
  name: string;
  venue: Venue;
  emailPreferences: EmailPreference[];
  inventoryOptions: InventoryOptions;
  shiftType: ShiftType;
  checkoutOptions: CheckoutOptions;
  printers: Printer[];
  deviceProfiles: DeviceProfile[];
  devices: Device[];
  isDeleted: boolean;
  operatingHours: OperatingHours;
  profitMargins: ProfitMargin[];
  catalogues: Catalogue[];
  pricingGroups: PricingGroup[];
  slug?: string;
  showHourlySplit?: boolean;
  showStaffStats?: boolean;
  merchantCode?: string;
  merchantId?: string;
  address?: Address;
  businessIdentifier?: string;
  email?: string;
  phoneNumber?: string;
  timezone?: string;
  /**
   * Start time of the store's business day, as 24-hour "HH:mm" in the store's
   * timezone (e.g. "04:00"). Used by shifts-go to decide when a business day
   * begins for auto-opening shifts. Defaults to "04:00" on store creation.
   */
  businessDayStartTime?: string;
  payStoreRefs?: PayStoreRef;
  merchantSettings?: MerchantSettings;
  paySalesChannels?: PaySalesChannelStatus;
  payMerchantConfig?: PayMerchantConfig;
  enableLocalComms?: boolean;
  priceListId?: string;
  products?: Product[];
  variants?: Variant[];
}
export interface Printer {
  id: string;
  name: string;
  code?: string;
  brand?: PrinterBrand;
  ipAddress?: string;
  port?: number;
  bdAddress?: string;
  slug?: string;
  series?: PrinterSeries;
  printerType: PrinterType;
  isKOT: boolean;
  store: Store;
  isActive: boolean;
  fallback?: {
    autoRedirectEnabled: boolean;
    printers: FallbackPrinter[];
  };
  deviceId?: string;
  serialPort?: string;
  serialBaud?: number;
  serialParity?: string;
  serialDataBits?: number;
  usbVendorId?: string;
  usbProductId?: string;
  usbSerialNumber?: string;
  chunkSize?: number;
}

export interface FallbackPrinter {
  priority: number;
  printer: Printer;
}

export interface PrinterOptions {
  printReceipt: boolean;
  emailReceipt: boolean;
}

export interface DisplayAsset {
  position: number;
  uri: string;
}

export interface DisplayScreen {
  mediaType?: MediaType;
  logoUri?: string;
  backgroundUri?: string;
  assets?: DisplayAsset[];
}

export interface DeviceProfileBrandingHeader {
  color?: string;
  uri?: string;
}

export interface DeviceProfileBranding {
  brandColor?: string;
  theme?: ThemeType;
  header?: DeviceProfileBrandingHeader;
  idleScreen?: DisplayScreen;
  cartScreen?: DisplayScreen;
}

export interface DeviceProfile {
  id: string;
  name: string;
  store: Store;
  menu: Catalogue;
  menuLayoutId?: string; // For Enhanced Menu V2 , need to select layout separately
  functionMap: FunctionMap;
  orderTypes: OrderType[];
  receiptPrintOption: ReceiptPrintOption;
  printerOptions: PrinterOptions;
  lockScreenTimeout: number;
  enableAudio: boolean;
  signatureAndTipping: SignatureAndTippingOption;
  enableCashManagement: boolean;
  enableFloorView: boolean;
  enableAllergens: boolean;
  enableBarcodeScan: boolean;
  enableCollapseSentItems: boolean;
  hideCartTotal: boolean;
  hideSendButton: boolean;
  showCartKeypad: boolean;
  autoRestartHour?: number;
  defaultCustomerSearchMode?: CustomerSearchMode;
  defaultSaleScreen: AppScreen;
  postSaleScreen: AppScreen;
  status: DeviceProfileStatus;
  isDeleted: boolean;
  defaultOrderType: OrderType;
  isActive: boolean;
  isDefault: boolean;
  devices: Device[];
  sections: Section[];
  enableCourses: boolean;
  enableSeatManagement: boolean;
  enableWaiterAssignment: boolean;
  enableHideZeroPrices: boolean;
  enableTareWeight: boolean;
  enablePinOnlyLogin: boolean;
  enableComplimentaryMode: boolean;
  enableOnlineOrders: boolean;
  enableQRPrint: boolean;
  enableKitchenBuzzer: boolean;
  orgLogo?: string;
  backgroundImage?: string;
  promoImages?: string[];
  showPrinterProfileInDocket?: boolean;
  printZeroValueItems?: boolean;
  printZeroValueModifiers?: boolean;
  printNotes?: boolean;
  mode?: DeviceMode;
  pricingGroupId?: string;
  orderIdentifier?: OrderIdentifier;
  promptGuestCount?: boolean;
  shouldAskForOrderType?: boolean;
  takingOrders?: boolean;
  payAtTable?: boolean;
  enableDarkMode?: boolean;
  showPriceOnTiles?: boolean;
  productsGridSize?: GridSize;
  optionsGridSize?: GridSize;
  customerDisplay?: DeviceProfileBranding;
  branding?: DeviceProfileBranding;
  showStatusBar?: boolean;
  showHoldBtn?: boolean;
  maxSplitPayments?: number;
  hideSplitByProduct?: boolean;
  hideSplitBySeat?: boolean;
  hideQuickCashOptions?: boolean;
}

export interface TokenSettings {
  tokenRange?: {
    start?: number;
    end?: number;
  };
  resetTokenNumberOnShiftClosure?: boolean;
  onlyPrintTokenOnReceipts?: boolean;
  // For kiosk device
  resetTokenNumberOnEndOfDay?: boolean;
}

export type NFCPosition =
  | 'TOP_LEFT'
  | 'TOP_CENTER'
  | 'TOP_RIGHT'
  | 'CENTER'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_CENTER'
  | 'BOTTOM_RIGHT'
  | 'LEFT_CENTER'
  | 'RIGHT_CENTER';

export type PinAlignment = 'LEFT' | 'RIGHT';

export interface Tap2PayDisplaySettings {
  enableRotation?: boolean;
  nfcKioskPosition?: NFCPosition;
  pinAlignment?: PinAlignment;
}

// Per-device overrides for the store's CheckoutOptions; unset fields fall back to the store.
export interface DeviceCheckoutOptions {
  enableTips?: boolean;
}

export interface Device {
  id: string;
  name: string;
  details: string;
  uuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  appVersion: string;
  store: Store;
  deviceProfile: DeviceProfile;
  salesPrefix: string;
  returnPrefix: string;
  cashDrawer: string;
  paymentTerminal: Terminal;
  previousOrder?: Order;
  printingOptions?: PrintingOptions[];
  pricingGroupId?: string;
  pushNotificationToken?: string;
  isOnline?: boolean;
  lastDisconnectedAt: number;
  customerDisplay?: CustomerDisplay;
  poiId?: string;
  lastOrderNumber?: string;
  mode: DeviceMode;
  disablePolling?: boolean;
  enablePaycloudPayments?: boolean;
  enableDeeplinkPayments?: boolean;
  enableTap2PayEmbedded?: boolean;
  payCloudConfig?: { terminalId?: string };
  tap2PayDisplaySettings?: Tap2PayDisplaySettings;
  checkoutOptions?: DeviceCheckoutOptions;
  isTokenNumberActive?: boolean;
  tokenSettings?: TokenSettings;
  trainingMode?: boolean;
  currentTokenNumber?: number;
  useCloudPayment?: boolean;
  linkCdsToPayApp?: boolean;
  config?: DeviceConfig;
  deviceMetadata?: DeviceMeta;
  configVersion?: number;
}

export interface DeviceMeta {
  browser?: string;
  os?: string;
  appVersion?: string;
  userAgent?: string;
  platform?: string;
  deviceType?: string;
  screenResolution?: string;
  timezone?: string;
}

export interface OrbDeviceConfig {
  type: DeviceMode;
  orderTypeIds?: string[]; //can add orderType[] field later
  printerProfileIds?: string[]; //can add PrinterProfile[] field later
  autoClearTimeout?: OrbAutoClearTimeout;
  theme?: Theme;
}

export type DeviceConfig = OrbDeviceConfig | null;

export interface Terminal {
  id: string;
  name: string;
  uuid: string;
  store: Store;
  device: Device;
  ipAddress?: string;
  enableLocalPayment?: boolean;
}

export interface CustomerDisplay {
  id: string;
  name: string;
  deviceUuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  lastLoginAt?: number;
  appVersion?: string;
  platform?: string;
  connectedPosDevice?: Device;
}

export interface KitchenDisplay {
  id: string;
  name: string;
  details: string;
  uuid?: string;
  isPaired?: boolean;
  deviceCode?: string;
  store: Store;
  printerProfiles?: PrinterProfile[];
  lastDisconnectedAt: number;
  mode: KitchenDisplayMode;
  notificationConfig: KdsNotificationConfig;
  timerMilestones?: KdsTimerMilestone[];
  overtimeMinutes?: number;
  printer?: Printer;
  autoPrintOnCompletion?: boolean;
  singleItemPrinting?: boolean;
  layoutStrategy?: LayoutStrategyConfig;
  groupItemsByStatus?: boolean;
  showNotifiersInline?: boolean;
  isCourseCallEnabled?: boolean;
  showStaffName?: boolean;
  appVersion?: string;
  platform?: string;
  devices?: string[];
  sections?: string[];
  modifierStyles?: KdsModifierStyle[];
}
